import { GridColDef, DataGrid } from "@mui/x-data-grid";
import {
  OperationType,
  SwapExecutionType,
} from "@wallet-manager/node-types/dist/src/postgres/const";
import { useQuery } from "react-query";
import { getWalletManagement } from "src/api/merchant";
import { CustomPagination } from "src/components/CustomPagination";
import DialogInOne from "src/components/DialogInOne";
import { NoRowsOverlay } from "src/components/NoRowsOverlay";
import { WalletTypeIconWithAddress } from "src/components/WalletTypeIconWithAddress";
import { useTranslation } from "src/hooks";
import useColumnHide, {
  getColumnHideConfig,
  ColumnHiddenMessage,
} from "src/hooks/useColumnHide";
import {
  dataGridDefaults,
  enumCreatorType,
  tableConfig,
} from "src/utils/constant";
import { toDisplayTime, displayAmount } from "src/utils/helper";
import { useGenGridCol } from "src/utils/HelperComp";
import { useZusDialogStore } from "src/zustand/store";
import SwapOperationDetails from "./components/SwapOperationDetails";
import { Enum, getTabApis } from "../../MerchantOperationList";
import LoadingDialog from "src/components/LoadingDialog";
import { IoperationTab } from "../../types/IoperationTab";

function OperationDetailsDialog(props: { operationTab: IoperationTab }) {
  const zusDialog = useZusDialogStore();
  const matchedDialog = zusDialog.match("recordDialog");
  const { operationTab } = props;
  const apiParams = zusDialog.meta;
  const onPageChange = (page: number) =>
    zusDialog.setMeta({ ...zusDialog.meta, page });
  const tabApi = getTabApis(operationTab);
  const listRes = useQuery({
    queryKey: ["getOperationDetails", apiParams],
    queryFn: () => tabApi.opDetail(apiParams),
    enabled: matchedDialog,
    onSettled: () => zusDialog.closeExtra(),
  });
  const { rows = [], count = 0 } = listRes.data || {};
  const newRows = rows.map((item) => ({
    ...item,
    version: item.wallet_version,
    created_date: toDisplayTime(item.created_date),
    last_modified_date: toDisplayTime(item.last_modified_date),
  }));
  const wallet_addresses_set = new Set<string>();
  newRows.forEach((item) => wallet_addresses_set.add(item.to_address));

  const { t, translate } = useTranslation("operations");
  const te = (key: string) => translate(`enumConstants.${key}`);

  const getParams = () => {
    const { chain_type, chain_id } = zusDialog.meta;

    return {
      chain_type,
      chain_id,
      wallet_address: Array.from(wallet_addresses_set),
      pageSize: 50,
      page: 0,
    };
  };

  const params = getParams();

  const listWalletAddressIconRes = useQuery({
    queryKey: ["getWalletManagement", params],
    queryFn: () => getWalletManagement(params),
    enabled: matchedDialog,
    onSettled: () => zusDialog.closeExtra(),
  });

  const list = listWalletAddressIconRes.data;

  const ToAddressWithWalletIcon = (props: any) => {
    const { wallet_type, display_name, smart_account_type } = list?.rows?.find(
      (item: any) => item.wallet_address === props.row.to_address,
    ) || { display_name: "", wallet_type: -1 };

    return (
      <WalletTypeIconWithAddress
        isCopy={true}
        walletObjFromDatabase={{
          wallet_type,
          display_name,
          smart_account_type,
        }}
        walletAddress={props.row.to_address}
      />
    );
  };

  const { creator_type = "" } = zusDialog.meta || {};

  const displayCreatorType = te(enumCreatorType[creator_type]);

  const details = newRows?.[0];
  const operationType = details?.operation_type;

  const isSwap = operationType === OperationType.Swap;
  const isApproveAllowance = operationType === OperationType.ApproveAllowance;

  const receivingInfo = [
    useGenGridCol("to_address", t("to_address"), {
      renderCell: ToAddressWithWalletIcon,
      minWidth: 350,
    }),
    useGenGridCol("to_wallet_tag", t("to_wallet_tag"), {}),
  ];

  const spenderInfo = [
    useGenGridCol("spender_address", t("spender_address"), {
      minWidth: 350,
    }),
    useGenGridCol(
      "spender_contract_version",
      t("spender_contract_version"),
      {},
    ),
    useGenGridCol("spender_contract_id", t("spender_contract_id"), {}),
  ];

  const detailColumns: GridColDef[] = [
    useGenGridCol("batch_id", t("batch_id"), {}),
    useGenGridCol("creator_type", t("creator_type"), {
      valueGetter: () => displayCreatorType,
    }),
    useGenGridCol("operation_seq", t("operation_seq"), {
      minWidth: 130,
    }),
    useGenGridCol("merchant_order_id", t("merchant_order_id"), {
      minWidth: 120,
    }),
    useGenGridCol("version", t("version")),
    useGenGridCol("wallet_path", t("path"), {
      minWidth: 120,
    }),
    useGenGridCol("operation_type", t("operation_type"), {
      valueGetter: (params) => {
        const type = Enum.operationType[params.row?.operation_type];
        return translate(`enumOperationType.${type}`);
      },
    }),
    useGenGridCol("from_address", t("from_address"), {}),
    ...(isApproveAllowance ? spenderInfo : receivingInfo),
    useGenGridCol("asset_name", t("asset_name"), {
      minWidth: 120,
    }),
    useGenGridCol("amount", t("amount"), {
      minWidth: 120,
      valueGetter: (params) => {
        return displayAmount(params.row.amount, params.row.decimals);
      },
    }),
    useGenGridCol("created_date", t("created_date")),
    useGenGridCol("last_modified_date", t("last_modified_date"), {}),
  ];
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    detailColumns,
    getColumnHideConfig("detail"),
  );

  const isLoadingApi = listRes.isLoading || listWalletAddressIconRes.isLoading;

  if (!matchedDialog) return null;

  const getContent = () => {
    if (isSwap) {
      const mappedOperationType = Enum.operationType[details?.operation_type];

      const getTotalAmount = displayAmount(
        details?.swap_operation?.amount_get,
        details?.swap_operation?.decimals_get,
      );
      const getCurrency = details?.swap_operation?.asset_name_get;

      const payTotalAmount = displayAmount(details?.amount, details?.decimals);

      const payCurrency = details?.asset_name;

      const labelValueArr = [
        { label: "batch_id", value: details?.batch_id },
        { label: "creator_type", value: displayCreatorType },
        { label: "operation_sequence", value: details?.operation_seq },
        {
          label: "merchant_order_id",
          value: details?.merchant_order_id,
        },
        {
          label: "wallet_version",
          value: details?.wallet_version,
        },
        {
          label: "swap_path",
          value: details?.swap_operation?.swap_path,
        },
        {
          label: "operation_type",
          value: translate(`enumOperationType.${mappedOperationType}`),
        },
        {
          label: "wallet_address",
          value: details?.from_address,
        },
        {
          label: "swap_execution_type",
          value: te(SwapExecutionType[details?.swap_operation?.execution_type]),
        },
        {
          label: "pay_currency",
          value: payCurrency,
        },
        {
          label: "pay_total_amount",
          value: payTotalAmount,
        },
        {
          label: "get_currency",
          value: getCurrency,
        },
        {
          label: "get_total_amount",
          value: getTotalAmount,
        },
        {
          label: "creation_time",
          value: details?.created_date,
        },
        {
          label: "last_modified_time",
          value: details?.last_modified_date,
        },
      ];

      return <SwapOperationDetails labelValueArr={labelValueArr} />;
    }

    return (
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={newRows}
        rowCount={count}
        columns={detailColumns}
        onPageChange={onPageChange}
        rowsPerPageOptions={tableConfig.rowsPerPageOptions}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    );
  };

  return (
    <>
      <LoadingDialog forceOpen={isLoadingApi} />
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DialogInOne
        {...{
          title: t("operationDetails"),
          self: {
            open: matchedDialog && !isLoadingApi,
            onClose: zusDialog.close,
          },
          content: getContent(),
          onCancel: zusDialog.close,
          isConfirmHidden: true,
          onConfirm: () => {},
          rest: { maxWidth: "md" },
        }}
      />
    </>
  );
}

export default OperationDetailsDialog;
